import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flame, Award, BookOpen, Sparkles } from "lucide-react";
import api from "@/lib/api";

const BADGE_META = {
  three_day_streak: { label: "3-day streak", desc: "Learned three days in a row." },
  week_warrior: { label: "Week warrior", desc: "Learned seven days in a row." },
  curious_learner: { label: "Curious learner", desc: "Finished 5 lessons." },
  homework_hero: { label: "Homework hero", desc: "Worked on homework 5 times." },
};

export default function Progress() {
  const { id } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get(`/students/${id}`), api.get(`/progress/${id}`)]);
      setStudent(s.data);
      setProgress(p.data);
    } catch {
      nav("/dashboard");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
  }, [load]);

  if (!student || !progress) return null;

  const subjectEntries = Object.entries(progress.subjects || {});
  const totalActivities = (progress.lessons_completed || 0) + (progress.homework_sessions || 0);
  const maxSubject = Math.max(1, ...subjectEntries.map(([, v]) => v));

  return (
    <main className="min-h-screen" style={{ background: "var(--tm-bg-2)" }} data-testid="progress-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6">
        <Link to={`/students/${id}/home`} className="tm-btn tm-btn-ghost mb-4" data-testid="progress-back">
          <ArrowLeft size={18} /> Back
        </Link>
        <h1 className="text-3xl sm:text-4xl font-black mb-6" style={{ fontFamily: "Nunito, sans-serif" }}>
          {student.name}'s progress
        </h1>

        <div className="grid sm:grid-cols-3 gap-5 mb-8" data-testid="progress-summary">
          <StatCard
            icon={Flame}
            title="Day streak"
            value={progress.streak_days || 0}
            testId="stat-streak"
          />
          <StatCard
            icon={Sparkles}
            title="Lessons finished"
            value={progress.lessons_completed || 0}
            testId="stat-lessons"
          />
          <StatCard
            icon={BookOpen}
            title="Homework sessions"
            value={progress.homework_sessions || 0}
            testId="stat-homework"
          />
        </div>

        <section className="tm-card mb-8" data-testid="subject-progress">
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Nunito, sans-serif" }}>
            Subjects and topics
          </h2>
          {subjectEntries.length === 0 ? (
            <p className="text-[color:var(--tm-ink-3)]">
              No activity yet. Start a lesson or a homework session.
            </p>
          ) : (
            <ul className="space-y-4">
              {subjectEntries.map(([s, count]) => {
                const pct = Math.round((count / maxSubject) * 100);
                return (
                  <li key={s} data-testid={`subject-${s.toLowerCase()}`}>
                    <div className="flex justify-between mb-1">
                      <span className="font-bold">{s}</span>
                      <span className="text-[color:var(--tm-ink-3)]">{count}</span>
                    </div>
                    <div
                      className="w-full rounded-full h-3 overflow-hidden"
                      style={{ background: "var(--tm-bg-3)" }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, background: "var(--tm-brand)" }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="tm-card" data-testid="badges-section">
          <div className="flex items-center gap-2 mb-3">
            <Award size={22} style={{ color: "var(--tm-brand)" }} />
            <h2 className="text-xl font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>Badges</h2>
          </div>
          {(!progress.badges || progress.badges.length === 0) ? (
            <p className="text-[color:var(--tm-ink-3)]">
              No badges yet. Keep learning to earn your first one.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {progress.badges.map((b) => {
                const meta = BADGE_META[b] || { label: b, desc: "" };
                return (
                  <div key={b} className="tm-card-soft flex gap-3 items-start" data-testid={`badge-${b}`}>
                    <div
                      className="p-2 rounded-xl"
                      style={{ background: "var(--tm-yellow)", color: "var(--tm-ink)" }}
                    >
                      <Award size={22} />
                    </div>
                    <div>
                      <p className="font-bold">{meta.label}</p>
                      <p className="text-sm text-[color:var(--tm-ink-3)]">{meta.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <p className="mt-6 text-sm text-[color:var(--tm-ink-3)]" data-testid="progress-total">
          Total learning moments: {totalActivities}
        </p>
      </div>
    </main>
  );
}

function StatCard({ icon: Icon, title, value, testId }) {
  return (
    <div className="tm-card" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="p-2 rounded-xl" style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}>
          <Icon size={18} />
        </div>
        <p className="text-sm font-bold uppercase tracking-wider text-[color:var(--tm-ink-3)]">{title}</p>
      </div>
      <p className="text-4xl font-black" style={{ fontFamily: "Nunito, sans-serif" }}>{value}</p>
    </div>
  );
}
